/**
 * AdminPaymentEvents — feed of every PayPal webhook event the system received.
 *
 * Backend endpoints:
 *   GET /api/paypal/webhook-events?event_type=&company_role=&verified=&skip=&limit=
 *   GET /api/paypal/webhook-events/{id}      (full payload + handler result)
 *
 * Each event is stored regardless of signature outcome (audit). Verified=false
 * events render as red rows (typical sign of mis-configured Webhook ID).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, RefreshCw, ShieldCheck, ShieldAlert, X, Filter, ChevronLeft, ChevronRight,
  CheckCircle2, AlertTriangle, Clock, RotateCcw, Globe, FileText, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  '', // all
  'CHECKOUT.ORDER.APPROVED', 'CHECKOUT.ORDER.COMPLETED',
  'PAYMENT.CAPTURE.COMPLETED', 'PAYMENT.CAPTURE.PENDING',
  'PAYMENT.CAPTURE.DENIED', 'PAYMENT.CAPTURE.REFUNDED',
  'PAYMENT.CAPTURE.REVERSED',
  'CUSTOMER.DISPUTE.CREATED', 'CUSTOMER.DISPUTE.RESOLVED',
];

const ROLE_FLAGS = { USA_COMPANY: '🇺🇸', UK_COMPANY: '🇬🇧', EU_COMPANY: '🇪🇺' };

function eventTypeTone(t) {
  if (!t) return 'bg-slate-100 text-slate-600';
  if (t.includes('COMPLETED')) return 'bg-emerald-100 text-emerald-700';
  if (t.includes('REFUND') || t.includes('REVERS')) return 'bg-amber-100 text-amber-800';
  if (t.includes('DENIED') || t.includes('FAILED')) return 'bg-red-100 text-red-700';
  if (t.includes('PENDING') || t.includes('APPROVED')) return 'bg-blue-100 text-blue-700';
  if (t.includes('DISPUTE')) return 'bg-rose-200 text-rose-900 font-bold';
  return 'bg-slate-100 text-slate-600';
}

export default function AdminPaymentEvents({ token, onBadgeShouldRefresh }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState('');
  const [companyRole, setCompanyRole] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState(''); // '' | 'true' | 'false'
  const [selected, setSelected] = useState(null);
  const [busyAck, setBusyAck] = useState(new Set());
  const [bulkAcking, setBulkAcking] = useState(false);
  // Total number of currently-failed + not-yet-acknowledged events
  // *in the whole DB* (not just the current page). We gate the
  // "Dismiss all failed" button on this so the button stays visible
  // whenever there is anything to dismiss, even when the current
  // page shows only verified rows.
  const [totalFailedUnacked, setTotalFailedUnacked] = useState(0);

  const loadTotalFailedUnacked = useCallback(async () => {
    try {
      const r = await fetch(
        `${API_URL}/api/paypal/webhook-events?verified=false&acknowledged=false&limit=1`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) return;
      const d = await r.json();
      setTotalFailedUnacked(d.total || 0);
    } catch (_) { /* silent — badge stays at previous value */ }
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    if (eventType)    params.set('event_type', eventType);
    if (companyRole)  params.set('company_role', companyRole);
    if (verifiedFilter) params.set('verified', verifiedFilter);
    try {
      const r = await fetch(`${API_URL}/api/paypal/webhook-events?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
      setTotal(j.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, skip, limit, eventType, companyRole, verifiedFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTotalFailedUnacked(); }, [loadTotalFailedUnacked]);

  // ── Acknowledge helpers ─────────────────────────────────────────
  // "Dismiss" a single stale/test event: still visible in the audit
  // log but no longer counted in the sub-tab badge. Optimistic update
  // patches the row's `acknowledged=true` locally so the visual state
  // flips instantly.
  const acknowledgeEvent = useCallback(async (evId) => {
    setBusyAck(prev => { const s = new Set(prev); s.add(evId); return s; });
    try {
      const r = await fetch(`${API_URL}/api/paypal/webhook-events/${evId}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        alert(`Dismiss failed: HTTP ${r.status}`);
        return;
      }
      setItems(prev => prev.map(it => it.id === evId ? { ...it, acknowledged: true } : it));
      if (onBadgeShouldRefresh) onBadgeShouldRefresh();
      loadTotalFailedUnacked();
    } catch (e) { alert(`Dismiss failed: ${e?.message || e}`); }
    finally {
      setBusyAck(prev => { const s = new Set(prev); s.delete(evId); return s; });
    }
  }, [token, onBadgeShouldRefresh]);

  const acknowledgeAllFailed = useCallback(async () => {
    // Confirm because this hides ALL unverified events at once.
    if (!window.confirm('Dismiss ALL failed webhook events? They stay in the audit log but the red badge goes away.')) return;
    setBulkAcking(true);
    try {
      const r = await fetch(`${API_URL}/api/paypal/webhook-events/acknowledge-all?only_failed=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        alert(`Bulk dismiss failed: HTTP ${r.status}`);
        return;
      }
      await load();
      if (onBadgeShouldRefresh) onBadgeShouldRefresh();
      loadTotalFailedUnacked();
    } catch (e) { alert(`Bulk dismiss failed: ${e?.message || e}`); }
    finally { setBulkAcking(false); }
  }, [token, load, onBadgeShouldRefresh, loadTotalFailedUnacked]);

  const stats = useMemo(() => {
    const ok = items.filter(i => i.verified).length;
    const failed = items.filter(i => !i.verified).length;
    return { ok, failed };
  }, [items]);

  return (
    <div className="max-w-[88rem] mx-auto p-4 md:p-6 space-y-4" data-testid="admin-payment-events">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl flex-1">
          <Globe className="w-5 h-5 text-blue-700 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">PayPal webhook event log</p>
            <p>Every signed event PayPal sends to <code className="bg-white/70 px-1 rounded font-mono text-xs">/api/paypal/webhook</code> is stored here for audit. Click any row for full payload + handler result.</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} data-testid="payment-events-refresh"
          className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-bold flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        {totalFailedUnacked > 0 && (
          <button
            onClick={acknowledgeAllFailed}
            disabled={bulkAcking}
            data-testid="payment-events-ack-all-failed"
            className="px-3 py-2 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
            title={`Mark every currently-failed row (${totalFailedUnacked} across all pages) as reviewed — badge goes away, rows stay in the audit log.`}
          >
            {bulkAcking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Dismiss all failed ({totalFailedUnacked})
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Event type</label>
          <select value={eventType} onChange={(e) => { setSkip(0); setEventType(e.target.value); }}
            data-testid="payment-events-filter-type"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t || '— all —'}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Company</label>
          <select value={companyRole} onChange={(e) => { setSkip(0); setCompanyRole(e.target.value); }}
            data-testid="payment-events-filter-company"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
            <option value="">— all —</option>
            <option value="USA_COMPANY">🇺🇸 USA</option>
            <option value="UK_COMPANY">🇬🇧 UK</option>
            <option value="EU_COMPANY">🇪🇺 EU</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Verified</label>
          <select value={verifiedFilter} onChange={(e) => { setSkip(0); setVerifiedFilter(e.target.value); }}
            data-testid="payment-events-filter-verified"
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
            <option value="">— all —</option>
            <option value="true">Verified ✓</option>
            <option value="false">Failed ✗</option>
          </select>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs">
          <span className="text-slate-500">Page {Math.floor(skip / limit) + 1}</span>
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded text-emerald-700"><ShieldCheck className="w-3 h-3" /> {stats.ok} ok</div>
          {stats.failed > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-red-50 rounded text-red-700"><ShieldAlert className="w-3 h-3" /> {stats.failed} failed</div>
          )}
        </div>
      </div>

      {/* Table — desktop only */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading events…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Filter className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No webhook events match these filters yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-2.5 py-2 text-left whitespace-nowrap">Time</th>
                <th className="px-2.5 py-2 text-left whitespace-nowrap">Event</th>
                <th className="px-2.5 py-2 text-left whitespace-nowrap">Company</th>
                <th className="px-2.5 py-2 text-center whitespace-nowrap">Sig</th>
                <th className="px-2.5 py-2 text-left whitespace-nowrap">Result</th>
                <th className="px-2.5 py-2 text-left whitespace-nowrap">Summary</th>
                <th className="px-2.5 py-2 text-right whitespace-nowrap w-[80px]"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(ev => (
                <tr key={ev.id}
                  onClick={() => setSelected(ev)}
                  data-testid={`payment-event-row-${ev.id}`}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${!ev.verified && !ev.acknowledged ? 'bg-red-50/40' : ''} ${ev.acknowledged ? 'opacity-60' : ''}`}>
                  <td className="px-2.5 py-2 text-xs text-slate-600 whitespace-nowrap">
                    {new Date(ev.received_at).toLocaleString()}
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${eventTypeTone(ev.event_type)}`}>{ev.event_type || '—'}</span>
                  </td>
                  <td className="px-2.5 py-2 text-xs max-w-[220px] truncate" title={ev.company_name || ''}>
                    {ev.company_name ? (
                      <span>{ROLE_FLAGS[ev.company_role] || '🌐'} {ev.company_name} <span className="text-slate-400">({ev.mode})</span></span>
                    ) : <span className="text-slate-400 italic">unknown</span>}
                  </td>
                  <td className="px-2.5 py-2 text-center whitespace-nowrap">
                    {ev.verified
                      ? <ShieldCheck className="w-4 h-4 text-emerald-600 inline" />
                      : <ShieldAlert className="w-4 h-4 text-red-600 inline" />}
                  </td>
                  <td className="px-2.5 py-2 text-xs whitespace-nowrap">
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[10px]">
                      {ev.handler_result?.status || '—'}
                    </span>
                  </td>
                  <td className="px-2.5 py-2 text-xs text-slate-500 max-w-[300px] truncate" title={ev.summary || ''}>{ev.summary}</td>
                  <td className="px-2.5 py-2 text-right whitespace-nowrap">
                    {ev.acknowledged ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500" title={ev.acknowledged_by ? `Dismissed by ${ev.acknowledged_by}` : 'Dismissed'}>
                        <CheckCircle2 className="w-3 h-3" /> dismissed
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); acknowledgeEvent(ev.id); }}
                        disabled={busyAck.has(ev.id)}
                        data-testid={`payment-event-dismiss-${ev.id}`}
                        title="Dismiss (mark as reviewed) — row stays in the audit log"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 hover:bg-slate-100 text-slate-600 text-[10px] font-bold disabled:opacity-40"
                      >
                        {busyAck.has(ev.id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        Dismiss
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-slate-500">Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0}
                data-testid="payment-events-prev"
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total}
                data-testid="payment-events-next"
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile card stack */}
      <div className="md:hidden space-y-3" data-testid="payment-events-mobile-cards">
        {loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading events…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            <Filter className="w-6 h-6 mx-auto mb-2 opacity-40" />
            No webhook events match these filters yet.
          </div>
        )}
        {!loading && items.map(ev => (
          <button
            key={ev.id}
            type="button"
            onClick={() => setSelected(ev)}
            data-testid={`payment-event-card-${ev.id}`}
            className={`block w-full text-left bg-white rounded-xl border ${!ev.verified ? 'border-red-300' : 'border-slate-200'} shadow-sm overflow-hidden active:scale-[0.99] transition-transform`}
          >
            <div className={`px-4 py-3 flex items-center justify-between gap-2 border-b border-slate-200 ${!ev.verified ? 'bg-red-50/60' : 'bg-gradient-to-r from-slate-50 to-slate-100'}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                  {new Date(ev.received_at).toLocaleString()}
                </p>
                <p className="mt-0.5">
                  <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${eventTypeTone(ev.event_type)}`}>
                    {ev.event_type || '—'}
                  </span>
                </p>
              </div>
              <div className="flex-shrink-0">
                {ev.verified
                  ? <ShieldCheck className="w-6 h-6 text-emerald-600" title="Signature verified" />
                  : <ShieldAlert className="w-6 h-6 text-red-600" title="Signature NOT verified" />}
              </div>
            </div>
            <dl className="px-4 py-2.5 text-sm divide-y divide-slate-100">
              <div className="flex justify-between py-1.5 gap-3 items-center">
                <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Company</dt>
                <dd className="text-slate-800 text-right min-w-0">
                  {ev.company_name ? (
                    <>
                      <span>{ROLE_FLAGS[ev.company_role] || '🌐'} {ev.company_name}</span>
                      <span className="text-slate-400 text-xs ml-1">({ev.mode})</span>
                    </>
                  ) : <span className="text-slate-400 italic">unknown</span>}
                </dd>
              </div>
              <div className="flex justify-between py-1.5 gap-3 items-center">
                <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Result</dt>
                <dd>
                  <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[10px] font-bold">
                    {ev.handler_result?.status || '—'}
                  </span>
                </dd>
              </div>
              {ev.summary && (
                <div className="py-1.5">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">Summary</dt>
                  <dd className="text-slate-700 text-[13px] leading-tight break-words">{ev.summary}</dd>
                </div>
              )}
            </dl>
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-center text-xs font-semibold text-cyan-700">
              Tap to view payload
            </div>
          </button>
        ))}
        {total > limit && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-slate-500">Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0}
                data-testid="payment-events-prev-mob"
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total}
                data-testid="payment-events-next-mob"
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <EventDetailModal event={selected} token={token} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}


function EventDetailModal({ event, token, onClose }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/paypal/webhook-events/${event.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setFull(await r.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [event.id, token]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="event-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-bold truncate">{event.event_type}</div>
              <div className="text-xs opacity-70">Event ID: <code className="font-mono">{event.event_id}</code></div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <Field label="Received at" value={new Date(event.received_at).toLocaleString()} />
          <Field label="Company" value={event.company_name ? `${ROLE_FLAGS[event.company_role] || ''} ${event.company_name} (${event.mode})` : '— unknown —'} />
          <Field label="Verified"
            value={event.verified
              ? <span className="text-emerald-700 font-bold flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Signature OK</span>
              : <span className="text-red-700 font-bold flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> Signature failed</span>} />
          <Field label="Handler result">
            <pre className="p-3 bg-slate-50 rounded-lg text-xs font-mono overflow-x-auto" data-testid="event-handler-result">
              {JSON.stringify(event.handler_result, null, 2)}
            </pre>
          </Field>
          <Field label="Summary" value={event.summary || '—'} />
          <Field label="Raw PayPal payload">
            {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
              <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg text-[10px] font-mono overflow-x-auto max-h-[40vh]" data-testid="event-raw-payload">
                {JSON.stringify(full?.raw, null, 2) || 'unavailable'}
              </pre>
            )}
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-bold text-slate-500 mb-0.5">{label}</div>
      {children !== undefined ? children : <div className="text-sm text-slate-800">{value}</div>}
    </div>
  );
}
